<script lang="ts">
	/**
	 * ApplyModal — design-partner application form.
	 *
	 * Native <dialog>: focus trap, ESC close, backdrop dismiss. Posts to
	 * /api/apply, where Resend forwards to the team inbox.
	 *
	 * Per spec §11 the form has eight fields:
	 *   name · email · practice_name · practice_size · specialty · ehr ·
	 *   hardest_question · website (honeypot)
	 *
	 * Plus `started_at` — a client-side timestamp the server uses for
	 * the fill-time spam check.
	 */

	import { tick } from 'svelte';
	import EkaviMark from './EkaviMark.svelte';
	import Signature from './Signature.svelte';
	import {
		trackApplyClose,
		trackApplySubmit,
		trackApplyError,
		trackFormFieldInteraction
	} from '$lib/analytics';
	import { PRACTICE_SIZES, type PracticeSize } from '$lib/practice-size';

	interface Props {
		open: boolean;
		source?: string;
	}

	let { open = $bindable(false) }: Props = $props();

	let dialogEl: HTMLDialogElement | undefined = $state();
	let firstInput: HTMLInputElement | undefined = $state();

	type FormState = 'idle' | 'submitting' | 'success' | 'error';
	let formState = $state<FormState>('idle');
	let formErrors = $state<Record<string, string>>({});
	let startedAt = $state(0);

	/* Controlled inputs */
	let name = $state('');
	let email = $state('');
	let practiceName = $state('');
	let practiceSize = $state<PracticeSize | ''>('');
	let specialty = $state('');
	let ehr = $state('');
	let hardest = $state('');
	let website = $state(''); // honeypot

	const SIZE_LABELS: Record<PracticeSize, string> = {
		solo: 'Solo (1 MD)',
		'2_4': '2–4 MDs',
		'5_10': '5–10 MDs',
		'11_25': '11–25 MDs',
		'26_49': '26–49 MDs',
		'50_plus': '50+ MDs'
	};

	function onFieldFocus(field: string) {
		trackFormFieldInteraction(field, 'focus');
	}
	function onFieldBlur(field: string, value: string) {
		trackFormFieldInteraction(field, value.trim().length > 0 ? 'blur_filled' : 'blur_empty');
	}

	$effect(() => {
		if (!dialogEl) return;
		if (open && !dialogEl.open) {
			try {
				dialogEl.showModal();
				startedAt = Date.now();
				tick().then(() => firstInput?.focus());
			} catch {
				/* dialog not yet attached */
			}
		} else if (!open && dialogEl.open) {
			try {
				dialogEl.close();
			} catch {
				/* already closed */
			}
		}
	});

	function closeWithReason(reason: 'dismiss' | 'backdrop' | 'escape') {
		if (formState !== 'success') trackApplyClose(reason);
		open = false;
		if (formState === 'success') {
			setTimeout(() => {
				name = '';
				email = '';
				practiceName = '';
				practiceSize = '';
				specialty = '';
				ehr = '';
				hardest = '';
				website = '';
				formState = 'idle';
				formErrors = {};
			}, 320);
		}
	}

	function handleClose() {
		closeWithReason('dismiss');
	}
	function onNativeClose() {
		closeWithReason('escape');
	}
	function onBackdropClick(e: MouseEvent) {
		if (e.target === dialogEl) closeWithReason('backdrop');
	}

	async function handleSubmit(e: SubmitEvent) {
		e.preventDefault();
		if (formState === 'submitting') return;

		formState = 'submitting';
		formErrors = {};

		try {
			const res = await fetch('/api/apply', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					name,
					email,
					practice_name: practiceName,
					practice_size: practiceSize,
					specialty,
					ehr,
					hardest_question: hardest,
					website,
					started_at: startedAt
				})
			});

			let data: { success?: boolean; errors?: Record<string, string> } = {};
			try {
				data = (await res.json()) as { success?: boolean; errors?: Record<string, string> };
			} catch {
				/* non-JSON response — fall through to generic error */
			}

			if (res.ok && data.success) {
				formState = 'success';
				trackApplySubmit({ has_practice: practiceName.trim().length > 0 });
				return;
			}

			formErrors = data.errors ?? {
				_server: 'Something went wrong. Please try again in a moment.'
			};
			formState = 'error';
			const firstField = Object.keys(formErrors).find((k) => k !== '_server');
			trackApplyError(
				firstField ? 'validation' : 'server',
				formErrors[firstField ?? '_server'] || 'unknown'
			);
		} catch (err) {
			formErrors = {
				_server: 'Network error. Please check your connection and try again.'
			};
			formState = 'error';
			trackApplyError('network', err instanceof Error ? err.message : 'network error');
		}
	}
</script>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<dialog
	bind:this={dialogEl}
	class="apply-dialog"
	aria-labelledby="apply-title"
	onclose={onNativeClose}
	onclick={onBackdropClick}
>
	<div class="modal" role="document" onclick={(e) => e.stopPropagation()} onkeydown={() => {}}>
		{#if formState !== 'success'}
			<header class="modal-head">
				<span class="brand">
					<span class="brand-mark" aria-hidden="true">
						<EkaviMark size={20} decorative maskIdSuffix="apply" />
					</span>
					<span class="brand-text">A note to Ekavi</span>
				</span>
				<button type="button" class="close" aria-label="Close" onclick={handleClose}>
					<svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
						<path
							d="M 4 4 L 16 16 M 16 4 L 4 16"
							stroke="currentColor"
							stroke-width="1.4"
							stroke-linecap="round"
							fill="none"
						/>
					</svg>
				</button>
			</header>

			<div class="modal-body">
				<h2 id="apply-title" class="modal-title">Write me in.</h2>
				<p class="modal-lede">Five seats. We read every note ourselves and reply within a day.</p>

				<form onsubmit={handleSubmit} class="apply-form" novalidate>
					<!-- Honeypot -->
					<div class="honeypot" aria-hidden="true">
						<label>
							Website (leave this empty)
							<input
								type="text"
								name="website"
								tabindex="-1"
								autocomplete="off"
								bind:value={website}
							/>
						</label>
					</div>

					<div class="field" class:has-error={formErrors.name}>
						<label for="apply-name">What should we call you?</label>
						<input
							id="apply-name"
							type="text"
							bind:this={firstInput}
							bind:value={name}
							required
							maxlength="200"
							autocomplete="name"
							onfocus={() => onFieldFocus('name')}
							onblur={() => onFieldBlur('name', name)}
						/>
						{#if formErrors.name}<p class="err">{formErrors.name}</p>{/if}
					</div>

					<div class="field" class:has-error={formErrors.email}>
						<label for="apply-email">Where can we reach you?</label>
						<input
							id="apply-email"
							type="email"
							bind:value={email}
							required
							maxlength="200"
							autocomplete="email"
							inputmode="email"
							onfocus={() => onFieldFocus('email')}
							onblur={() => onFieldBlur('email', email)}
						/>
						{#if formErrors.email}<p class="err">{formErrors.email}</p>{/if}
					</div>

					<div class="row-2">
						<div class="field" class:has-error={formErrors.practice_name}>
							<label for="apply-practice">Practice name</label>
							<input
								id="apply-practice"
								type="text"
								bind:value={practiceName}
								required
								maxlength="600"
								autocomplete="organization"
								onfocus={() => onFieldFocus('practice_name')}
								onblur={() => onFieldBlur('practice_name', practiceName)}
							/>
							{#if formErrors.practice_name}<p class="err">{formErrors.practice_name}</p>{/if}
						</div>

						<div class="field" class:has-error={formErrors.practice_size}>
							<label for="apply-size">Size</label>
							<select
								id="apply-size"
								bind:value={practiceSize}
								required
								onfocus={() => onFieldFocus('practice_size')}
							>
								<option value="" disabled>Select…</option>
								{#each PRACTICE_SIZES as size (size)}
									<option value={size}>{SIZE_LABELS[size]}</option>
								{/each}
							</select>
							{#if formErrors.practice_size}<p class="err">{formErrors.practice_size}</p>{/if}
						</div>
					</div>

					<div class="row-2">
						<div class="field" class:has-error={formErrors.specialty}>
							<label for="apply-specialty">Specialty</label>
							<input
								id="apply-specialty"
								type="text"
								bind:value={specialty}
								required
								maxlength="600"
								placeholder="orthopedics, primary care, …"
								onfocus={() => onFieldFocus('specialty')}
								onblur={() => onFieldBlur('specialty', specialty)}
							/>
							{#if formErrors.specialty}<p class="err">{formErrors.specialty}</p>{/if}
						</div>

						<div class="field" class:has-error={formErrors.ehr}>
							<label for="apply-ehr">EHR</label>
							<input
								id="apply-ehr"
								type="text"
								bind:value={ehr}
								required
								maxlength="600"
								placeholder="athena, ECW, Tebra, …"
								onfocus={() => onFieldFocus('ehr')}
								onblur={() => onFieldBlur('ehr', ehr)}
							/>
							{#if formErrors.ehr}<p class="err">{formErrors.ehr}</p>{/if}
						</div>
					</div>

					<div class="field" class:has-error={formErrors.hardest_question}>
						<label for="apply-hardest">
							What's the hardest part of the back office right now?
							<span class="hint"
								>A specific moment beats a category. The more candid, the better.</span
							>
						</label>
						<textarea
							id="apply-hardest"
							bind:value={hardest}
							rows="4"
							maxlength="3000"
							placeholder="The hour lost on hold to UHC. The biller giving notice. The denial that came back at 4:50pm."
							onfocus={() => onFieldFocus('hardest_question')}
							onblur={() => onFieldBlur('hardest_question', hardest)}
						></textarea>
						{#if formErrors.hardest_question}<p class="err">{formErrors.hardest_question}</p>{/if}
					</div>

					{#if formErrors._server}
						<p class="server-err">{formErrors._server}</p>
					{/if}

					<div class="actions">
						<button
							type="button"
							class="btn-ghost"
							onclick={handleClose}
							disabled={formState === 'submitting'}
						>
							Never mind
						</button>
						<button type="submit" class="btn-primary" disabled={formState === 'submitting'}>
							{formState === 'submitting' ? 'Sending…' : 'Send letter'}
						</button>
					</div>

					<p class="fine">This note lands in our inbox and stays there. Reply within a day.</p>
				</form>
			</div>
		{:else}
			<div class="success">
				<div class="success-mark" aria-hidden="true">
					<EkaviMark size={36} decorative maskIdSuffix="apply-success" />
				</div>
				<h2 id="apply-title" class="success-title">We have your letter.</h2>
				<p class="success-body">
					We will write back within a day — to <strong>{email}</strong>.
				</p>
				<p class="success-sign">
					<Signature size="md" />
				</p>
				<button type="button" class="btn-primary btn-full" onclick={handleClose}> Close </button>
			</div>
		{/if}
	</div>
</dialog>

<style>
	/* ---- Reset native <dialog> ---- */
	.apply-dialog {
		border: none;
		padding: 0;
		background: transparent;
		max-width: none;
		max-height: none;
		color: inherit;
		overflow: visible;
		margin: auto;
	}
	.apply-dialog::backdrop {
		background: rgba(26, 27, 30, 0.55);
		backdrop-filter: blur(4px);
		-webkit-backdrop-filter: blur(4px);
	}

	/* ---- The modal card ---- */
	.modal {
		width: min(580px, calc(100vw - 24px));
		max-height: min(94vh, 880px);
		background: var(--ekavi-bone);
		border: var(--ekavi-stroke-hairline);
		border-radius: 0;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		position: relative;
	}
	.apply-dialog[open] .modal {
		animation: modal-in 200ms var(--ekavi-ease-out);
	}
	@keyframes modal-in {
		from {
			opacity: 0;
			transform: translateY(8px);
		}
		to {
			opacity: 1;
			transform: none;
		}
	}

	/* ---- Header ---- */
	.modal-head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: var(--ekavi-space-3) var(--ekavi-space-6);
		border-bottom: var(--ekavi-stroke-hairline);
		background: var(--ekavi-paper);
	}
	.brand {
		display: inline-flex;
		align-items: center;
		gap: var(--ekavi-space-2);
	}
	.brand-mark {
		color: var(--ekavi-charcoal);
		line-height: 0;
	}
	.brand-text {
		font-family: var(--ekavi-font-ui);
		font-style: italic;
		font-size: var(--ekavi-text-14);
		color: var(--ekavi-ink-2);
	}
	.close {
		appearance: none;
		background: transparent;
		border: var(--ekavi-stroke-hairline);
		width: 28px;
		height: 28px;
		color: var(--ekavi-ink-3);
		display: inline-flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition:
			border-color var(--ekavi-duration-quick) var(--ekavi-ease-out),
			color var(--ekavi-duration-quick) var(--ekavi-ease-out);
	}
	.close:hover {
		color: var(--ekavi-charcoal);
		border-color: var(--ekavi-charcoal);
	}
	.close:focus-visible {
		outline: 1px solid var(--ekavi-ochre);
		outline-offset: 2px;
	}

	/* ---- Body ---- */
	.modal-body {
		padding: var(--ekavi-space-9) var(--ekavi-space-9) var(--ekavi-space-6);
		overflow-y: auto;
	}
	@media (max-width: 520px) {
		.modal-body {
			padding: var(--ekavi-space-6) var(--ekavi-space-4);
		}
	}
	.modal-title {
		font-family: var(--ekavi-font-ui);
		font-weight: var(--ekavi-weight-kraftig);
		font-size: var(--ekavi-text-28);
		letter-spacing: var(--ekavi-track-headers);
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-2);
		line-height: var(--ekavi-leading-headers);
	}
	.modal-lede {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-16);
		color: var(--ekavi-ink-2);
		margin: 0 0 var(--ekavi-space-9);
		line-height: var(--ekavi-leading-body);
	}

	/* ---- Form ---- */
	.apply-form {
		display: flex;
		flex-direction: column;
		gap: var(--ekavi-space-6);
	}
	.row-2 {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--ekavi-space-6);
	}
	@media (max-width: 480px) {
		.row-2 {
			grid-template-columns: 1fr;
		}
	}
	.field {
		display: flex;
		flex-direction: column;
		gap: var(--ekavi-space-2);
	}
	.field label {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
		color: var(--ekavi-ink-1);
		line-height: var(--ekavi-leading-body);
		font-weight: var(--ekavi-weight-halbfett);
	}
	.hint {
		display: block;
		font-family: var(--ekavi-font-ui);
		font-style: italic;
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-3);
		font-weight: var(--ekavi-weight-buch);
		margin-top: 2px;
	}

	.field input,
	.field textarea,
	.field select {
		appearance: none;
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-16);
		color: var(--ekavi-ink-1);
		background: transparent;
		border: none;
		border-bottom: var(--ekavi-stroke-hairline);
		padding: var(--ekavi-space-2) 0;
		width: 100%;
		border-radius: 0;
		line-height: var(--ekavi-leading-body);
		transition: border-color var(--ekavi-duration-quick) var(--ekavi-ease-out);
	}
	.field textarea {
		resize: vertical;
		min-height: 84px;
		padding-bottom: var(--ekavi-space-3);
	}
	.field select {
		background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'><path d='M1 1l5 5 5-5' stroke='%231A1B1E' stroke-width='1.2' fill='none' stroke-linecap='round'/></svg>");
		background-repeat: no-repeat;
		background-position: right 4px center;
		background-size: 12px 8px;
		padding-right: 24px;
	}
	.field input::placeholder,
	.field textarea::placeholder {
		color: var(--ekavi-ink-3);
		font-style: italic;
	}
	.field input:focus,
	.field textarea:focus,
	.field select:focus {
		outline: none;
		border-bottom-color: var(--ekavi-ochre);
	}
	.field.has-error input,
	.field.has-error textarea,
	.field.has-error select {
		border-bottom-color: var(--ekavi-ochre);
	}
	.err {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ochre);
		margin: 0;
	}
	.server-err {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
		color: var(--ekavi-ink-1);
		margin: 0;
		padding: var(--ekavi-space-3) var(--ekavi-space-4);
		background: var(--ekavi-paper);
		border-left: 2px solid var(--ekavi-ochre);
	}

	.honeypot {
		position: absolute;
		left: -9999px;
		width: 1px;
		height: 1px;
		overflow: hidden;
		opacity: 0;
	}

	/* ---- Buttons ---- */
	.actions {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: var(--ekavi-space-3);
		margin-top: var(--ekavi-space-3);
		flex-wrap: wrap;
	}
	.btn-primary,
	.btn-ghost {
		appearance: none;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		height: 44px;
		padding: 0 var(--ekavi-space-6);
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
		font-weight: var(--ekavi-weight-halbfett);
		letter-spacing: 0.01em;
		border: 1px solid transparent;
		cursor: pointer;
		transition:
			background var(--ekavi-duration-quick) var(--ekavi-ease-out),
			color var(--ekavi-duration-quick) var(--ekavi-ease-out),
			border-color var(--ekavi-duration-quick) var(--ekavi-ease-out),
			opacity var(--ekavi-duration-quick) var(--ekavi-ease-out),
			transform var(--ekavi-duration-base) var(--ekavi-ease-out);
	}
	.btn-primary {
		background: var(--ekavi-charcoal);
		color: var(--ekavi-bone);
		border-color: var(--ekavi-charcoal);
	}
	.btn-primary:hover:not(:disabled),
	.btn-primary:focus-visible:not(:disabled) {
		opacity: 0.94;
		transform: translateY(-2px);
	}
	.btn-primary:focus-visible {
		outline: 1px solid var(--ekavi-ochre);
		outline-offset: 3px;
	}
	.btn-primary:disabled {
		opacity: 0.6;
		cursor: wait;
	}
	.btn-ghost {
		background: transparent;
		color: var(--ekavi-ink-2);
		border-color: var(--ekavi-hairline);
	}
	.btn-ghost:hover:not(:disabled),
	.btn-ghost:focus-visible:not(:disabled) {
		color: var(--ekavi-ink-1);
		border-color: var(--ekavi-charcoal);
		transform: translateY(-2px);
	}
	@media (prefers-reduced-motion: reduce) {
		.btn-primary,
		.btn-ghost {
			transition:
				background var(--ekavi-duration-quick) var(--ekavi-ease-out),
				color var(--ekavi-duration-quick) var(--ekavi-ease-out),
				border-color var(--ekavi-duration-quick) var(--ekavi-ease-out),
				opacity var(--ekavi-duration-quick) var(--ekavi-ease-out);
		}
		.btn-primary:hover:not(:disabled),
		.btn-primary:focus-visible:not(:disabled),
		.btn-ghost:hover:not(:disabled),
		.btn-ghost:focus-visible:not(:disabled) {
			transform: none;
		}
	}
	.btn-ghost:disabled {
		opacity: 0.4;
		cursor: not-allowed;
	}
	.btn-full {
		width: 100%;
	}

	.fine {
		font-family: var(--ekavi-font-ui);
		font-style: italic;
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-3);
		margin: var(--ekavi-space-2) 0 0;
	}

	/* ---- Success state ---- */
	.success {
		padding: var(--ekavi-space-12) var(--ekavi-space-9) var(--ekavi-space-9);
		text-align: center;
	}
	.success-mark {
		color: var(--ekavi-charcoal);
		margin: 0 auto var(--ekavi-space-4);
		line-height: 0;
	}
	.success-title {
		font-family: var(--ekavi-font-ui);
		font-weight: var(--ekavi-weight-kraftig);
		font-size: var(--ekavi-text-28);
		letter-spacing: var(--ekavi-track-headers);
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-3);
	}
	.success-body {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-16);
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-6);
		line-height: var(--ekavi-leading-body);
	}
	.success-body strong {
		font-weight: var(--ekavi-weight-halbfett);
	}
	.success-sign {
		margin: 0 0 var(--ekavi-space-9);
	}
	@media (max-width: 520px) {
		.success {
			padding: var(--ekavi-space-9) var(--ekavi-space-4) var(--ekavi-space-6);
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.apply-dialog[open] .modal {
			animation: none;
		}
		.close,
		.btn-primary,
		.btn-ghost,
		.field input,
		.field textarea,
		.field select {
			transition: none;
		}
	}
</style>
